import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'emans_chat_session_id';

const genSessionId = () => {
  const rnd = () => Math.random().toString(36).slice(2, 10);
  return `sess_${Date.now()}_${rnd()}`;
};

const AiChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text:
        "Hi there — I'm Pepper, Eman's AI helper. Ask me about products, bulk pricing, payment methods, shipping, or anything else!",
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = genSessionId();
      localStorage.setItem(SESSION_KEY, id);
    }
    setSessionId(id);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
      });
      const reply = res.data?.reply || "Sorry — didn't get that.";
      setMessages((m) => [...m, { role: 'assistant', text: reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text:
            "I'm having trouble right now. Please text 562-292-1095 and Eman will help you out.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Toggle button — bottom left so it doesn't fight Discord */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-chat-open"
          className="fixed bottom-6 left-6 z-50 group"
          style={{ transition: 'transform 0.3s ease' }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          aria-label="Open chat with Pepper"
        >
          <div
            className="relative p-4 rounded-full shadow-2xl flex items-center justify-center"
            style={{
              backgroundColor: '#D4A373',
              boxShadow: '0 10px 40px rgba(212, 163, 115, 0.5)',
            }}
          >
            <MessageCircle
              className="w-7 h-7"
              style={{ color: '#0C0A09' }}
              strokeWidth={2.2}
            />
            <span
              className="absolute -top-1 -right-1 w-3 h-3 rounded-full animate-pulse"
              style={{ backgroundColor: '#4ADE80', border: '2px solid #0C0A09' }}
            />
          </div>
          <div
            className="absolute left-full ml-3 top-1/2 transform -translate-y-1/2 whitespace-nowrap px-3 py-2 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            style={{
              backgroundColor: '#1A1513',
              color: '#F7F3EE',
              border: '1px solid #D4A373',
            }}
          >
            <span className="text-sm">
              Ask <span style={{ color: '#D4A373' }}>Pepper</span> anything
            </span>
          </div>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed z-50 bottom-6 left-6 flex flex-col rounded-xl overflow-hidden shadow-2xl"
          style={{
            width: 'min(380px, calc(100vw - 3rem))',
            height: 'min(560px, calc(100vh - 6rem))',
            backgroundColor: '#1A1513',
            border: '1px solid #362B25',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b"
            style={{
              borderColor: '#362B25',
              background:
                'linear-gradient(135deg, rgba(212,163,115,0.14), rgba(212,163,115,0.04))',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#D4A373' }}
              >
                <MessageCircle
                  className="w-5 h-5"
                  style={{ color: '#0C0A09' }}
                  strokeWidth={2.2}
                />
              </div>
              <div>
                <div
                  className="text-sm font-semibold"
                  style={{ color: '#F7F3EE', fontFamily: 'Manrope, sans-serif' }}
                >
                  Pepper
                </div>
                <div className="text-[10px] flex items-center gap-1" style={{ color: '#B5A79E' }}>
                  <span
                    className="w-1.5 h-1.5 rounded-full inline-block"
                    style={{ backgroundColor: '#4ADE80' }}
                  />
                  Online now
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="ai-chat-close"
              className="p-1.5 rounded hover:bg-[#0C0A09] transition"
              style={{ color: '#B5A79E' }}
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="ai-chat-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            style={{ backgroundColor: '#0C0A09' }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className="max-w-[85%] px-3.5 py-2.5 rounded-lg text-sm leading-relaxed whitespace-pre-wrap"
                  style={
                    m.role === 'user'
                      ? {
                          backgroundColor: '#D4A373',
                          color: '#0C0A09',
                          borderBottomRightRadius: 4,
                        }
                      : {
                          backgroundColor: '#1A1513',
                          color: '#F7F3EE',
                          border: '1px solid #362B25',
                          borderBottomLeftRadius: 4,
                        }
                  }
                  data-testid={`ai-chat-msg-${m.role}`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div
                  className="px-3.5 py-2.5 rounded-lg text-sm"
                  style={{
                    backgroundColor: '#1A1513',
                    color: '#B5A79E',
                    border: '1px solid #362B25',
                    borderBottomLeftRadius: 4,
                  }}
                >
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4A373] animate-bounce" style={{ animationDelay: '0s' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4A373] animate-bounce" style={{ animationDelay: '0.15s' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4A373] animate-bounce" style={{ animationDelay: '0.3s' }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div
            className="px-3 py-3 border-t flex items-end gap-2"
            style={{ borderColor: '#362B25', backgroundColor: '#1A1513' }}
          >
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about products, bulk, payment…"
              data-testid="ai-chat-input"
              className="flex-1 px-3 py-2 rounded resize-none text-sm placeholder:text-[#7A6E66] focus:outline-none focus:border-[#D4A373]"
              style={{
                backgroundColor: '#0C0A09',
                color: '#F7F3EE',
                border: '1px solid #362B25',
                maxHeight: 90,
              }}
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              data-testid="ai-chat-send"
              className="p-2.5 rounded transition disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#D4A373', color: '#0C0A09' }}
              aria-label="Send message"
            >
              <Send className="w-4 h-4" strokeWidth={2.2} />
            </button>
          </div>
          <div
            className="text-[10px] text-center py-1.5"
            style={{
              color: '#7A6E66',
              backgroundColor: '#1A1513',
              borderTop: '1px solid #362B25',
            }}
          >
            AI answers only — for orders text 562-292-1095
          </div>
        </div>
      )}
    </>
  );
};

export default AiChatWidget;
